import httpClient from '@/utils/httpClient';

export const getURL = () => {
  const url =
    process?.env?.URL && process.env.URL !== ''
      ? process.env.URL
      : process?.env?.VERCEL_URL && process.env.VERCEL_URL !== ''
        ? process.env.VERCEL_URL
        : 'http://localhost:3000';
  return url.includes('http') ? url : `https://${url}`;
};

export const postData = async ({
  url,
  token,
  data = { price: '', isUpgrade: false, flow_data_type: '' },
}: {
  url: string;
  data?: {
    price?: string;
    isUpgrade?: boolean;
    flow_data_type?: string;
    promotionCode?: string;
    withTrial?: boolean;
  };
  token: string;
}) => {
  console.log('posting,', url, token, data);

  const res = await httpClient.post(url, JSON.stringify(data));

  if (!res.data) {
    console.log('Error in postData', { url, token, data, res });

    throw Error(res.statusText);
  }

  return res.data;
};

export const toDateTime = (secs: number) => {
  return new Date(secs * 1000);
};

/**
 * Compute the next monthly limits-renewal date from a subscription's
 * current_period_start.
 *
 * All math is done in UTC on purpose. Using local-time getMonth()/setMonth()
 * — as the billing and usage pages used to — makes the displayed date depend
 * on the viewer's timezone: for viewers in the Americas (negative UTC offset)
 * a midnight-UTC current_period_start falls on the previous day locally, which
 * shifted the shown date forward by a day or two and made the billing and
 * usage pages disagree. Computing in UTC keeps the result identical everywhere.
 *
 * The day-of-month is clamped to the last day of the target month so
 * end-of-month subscriptions (e.g. the 31st) don't skip (Jan 31 -> Feb 28,
 * not Mar 3), mirroring the clamping the cron performs when it actually resets
 * the limits.
 *
 * The UTC time-of-day is preserved. Yearly subscriptions are normalized to
 * midnight UTC by the /api/update-current-period-start cron, but monthly
 * subscriptions keep the actual Stripe billing-cycle time, and that is when
 * their limits really reset — so we must not truncate it to 00:00:00Z.
 */
export const getNextRenewalDate = (
  currentPeriodStart: string | number | Date,
): Date => {
  const start = new Date(currentPeriodStart);
  const year = start.getUTCFullYear();
  const month = start.getUTCMonth();
  const day = start.getUTCDate();
  // Day 0 of (month + 2) is the last day of the target month (month + 1).
  const lastDayOfTargetMonth = new Date(
    Date.UTC(year, month + 2, 0),
  ).getUTCDate();
  const renewalDay = Math.min(day, lastDayOfTargetMonth);
  return new Date(
    Date.UTC(
      year,
      month + 1,
      renewalDay,
      start.getUTCHours(),
      start.getUTCMinutes(),
      start.getUTCSeconds(),
      start.getUTCMilliseconds(),
    ),
  );
};

/**
 * When a subscription's plan limits next reset. Monthly plans reset when Stripe
 * renews them (the anchor time). Yearly plans are reset by the
 * update-current-period-start cron, which stores the renewal as midnight UTC on
 * the renewal date, so their reset is midnight UTC even in the first month,
 * when current_period_start still holds Stripe's anchor time.
 */
export const toUtcResetDate = (d: Date) =>
  new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));

export const getNextLimitResetDate = (
  currentPeriodStart: string | number | Date,
  interval?: string | null,
): Date => {
  const next = getNextRenewalDate(currentPeriodStart);
  return interval === 'year' ? toUtcResetDate(next) : next;
};

export const formatMoney = (amount: number, currency: string) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
  }).format((amount || 0) / 100);
};
